import classnames from '@app/utils/classnames';
import AnimateHeight from '@store/components/AnimateHeight';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CheckoutFormModel } from '../schema';
import { ICheckoutAddress } from '../types';

interface IProps {
  addresses?: ICheckoutAddress[];
  countries: { [k: string]: string };
}

export default function ShippingAddressFieldset({ addresses, countries }: IProps) {
  const { t } = useTranslation();

  const { control, register, formState, setValue } = useFormContext<CheckoutFormModel>();
  const isDifferentShipping = useWatch({ name: 'customer.differentShipping', control });

  return (
    <div className="max-w-md w-full grid-cols-1 items-center">
      <div className="grid grid-cols-1 gap-y-2 py-4">
        <div className="flex items-center gap-x-2 py-2 text-left text-gray-500 text-xs">
          <span className="icon text-base text-blue-700">
            <i className="fa-solid fa-circle-info fa-lg"></i>
          </span>
          <span>{t('CheckoutShippingAddressInformation')}</span>
        </div>

        <button
          className={classnames('btn btn-primary cursor-pointer', {
            'btn-inverted': !isDifferentShipping,
          })}
          type="button"
          onClick={() => setValue('customer.differentShipping', !isDifferentShipping)}>
          <input type="checkbox" className="hidden" {...register('customer.differentShipping')} />
          <span>
            {t(isDifferentShipping ? 'DeliverToBillingAddress' : 'DeliverToAnotherAddress')}
          </span>
        </button>
      </div>

      <AnimateHeight isOpen={isDifferentShipping}>
        <div className="pt-4">
          <div className="form-fields">
            {addresses && addresses.length > 0 && (
              <div className="form-field">
                <label htmlFor="addressId" className="required">
                  {t('ShippingAddress')}
                </label>
                <div className="input">
                  <select
                    {...register('customer.shippingAddress.addressId')}
                    autoComplete="off"
                    className="w-full"
                    onChange={e => {
                      if (!e.target.value) {
                        setValue('customer.shippingAddress.addressId', '');
                        return;
                      }

                      const address = addresses.find(a => a.addressId === e.target.value);
                      if (!address) return;

                      setValue('customer.shippingAddress', address, { shouldValidate: true });
                      setValue('customer.differentShipping', isDifferentShipping);
                    }}>
                    <option value="">Vyberte</option>
                    {addresses.map(a => (
                      <option key={a.addressId} value={a.addressId}>{`${a.street}, ${a.postCode} ${
                        a.town
                      }, ${countries[a.countryCode]}`}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="form-field">
              <label htmlFor="shippingAddress.street" className="required">
                {t('Street')}
              </label>
              <div className="input">
                <input
                  type="text"
                  autoComplete="street-address"
                  className="w-full as-text"
                  {...register('customer.shippingAddress.street')}
                  onChange={e => {
                    setValue('customer.shippingAddress.addressId', '');
                    setValue('customer.shippingAddress.street', e.target.value);
                  }}
                />
                {formState.errors?.customer?.shippingAddress?.street?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.shippingAddress.street.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="shippingAddress.town" className="required">
                {t('Town')}
              </label>
              <div className="input">
                <input
                  type="text"
                  autoComplete="address-level2"
                  className="w-full as-text"
                  {...register('customer.shippingAddress.town')}
                  onChange={e => {
                    setValue('customer.shippingAddress.addressId', '');
                    setValue('customer.shippingAddress.town', e.target.value);
                  }}
                />
                {formState.errors?.customer?.shippingAddress?.town?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.shippingAddress.town.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="postCode" className="required">
                {t('PostCode')}
              </label>
              <div className="input">
                <input
                  type="text"
                  autoComplete="postal-code"
                  className="w-full as-text"
                  {...register('customer.shippingAddress.postCode')}
                  onChange={e => {
                    setValue('customer.shippingAddress.addressId', '');
                    setValue('customer.shippingAddress.postCode', e.target.value);
                  }}
                />
                {formState.errors?.customer?.shippingAddress?.postCode?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.shippingAddress.postCode.message)}
                  </span>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="countryCode" className="required">
                {t('Country')}
              </label>
              <div className="input">
                <select
                  autoComplete="country"
                  className="w-full"
                  {...register('customer.shippingAddress.countryCode')}
                  onChange={e => {
                    setValue('customer.shippingAddress.addressId', '');
                    setValue('customer.shippingAddress.countryCode', e.target.value);
                  }}>
                  <option value="">Vyberte</option>
                  {Object.keys(countries).map(code => (
                    <option key={code} value={code}>
                      {countries[code]}
                    </option>
                  ))}
                </select>
                {formState.errors?.customer?.shippingAddress?.countryCode?.message && (
                  <span className="field-validation-error">
                    {t(formState.errors.customer.shippingAddress.countryCode.message)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </AnimateHeight>
    </div>
  );
}
